import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, UserPlus, Pencil, Trash2, KeyRound, User, RefreshCw, Wallet, Download, Upload, Database } from "lucide-react";
import { toast } from "sonner";
import { money } from "@/lib/format";

const empty = { email: "", name: "", password: "", permissions: [] };

export default function PersonnelManagement() {
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user id or null (create)
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delUser, setDelUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [preventNeg, setPreventNeg] = useState(false);
  const [prefSaving, setPrefSaving] = useState(false);

  const togglePreventNeg = async () => {
    const next = !preventNeg;
    setPrefSaving(true);
    try {
      await api.setPreferences({ prevent_negative_balance: next });
      setPreventNeg(next);
      toast.success(next ? "Negatif bakiye koruması açıldı" : "Negatif bakiye koruması kapatıldı");
    } catch (e) {
      toast.error("Ayar kaydedilemedi");
    } finally {
      setPrefSaving(false);
    }
  };

  const doRebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.rebuildBalances();
      setRebuildResult(r);
      toast.success("Bakiyeler yeniden hesaplandı");
      setRebuildOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setRebuilding(false);
    }
  };

  const [backuping, setBackuping] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const doBackup = async () => {
    setBackuping(true);
    try {
      await api.backupDb();
      toast.success("Yedek indirildi");
    } catch (e) {
      toast.error("Yedekleme başarısız");
    } finally {
      setBackuping(false);
    }
  };

  const doRestore = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const r = await api.restoreDb(fd);
      toast.success(`Geri yüklendi: ${r.collections} koleksiyon`);
      setRestoreFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri yükleme başarısız");
    } finally {
      setRestoring(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.listUsers(), api.permissionsList()]);
      setUsers(u);
      setPerms(p);
      try {
        const pref = await api.getPreferences();
        setPreventNeg(!!pref.prevent_negative_balance);
      } catch (e) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setDlgOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({ email: u.email, name: u.name, password: "", permissions: u.permissions || [] });
    setDlgOpen(true);
  };

  const togglePerm = (key) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((k) => k !== key)
        : [...f.permissions, key],
    }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.updateUser(editing, { name: form.name, permissions: form.permissions });
        toast.success("Personel güncellendi");
      } else {
        await api.createUser({
          email: form.email,
          name: form.name,
          password: form.password,
          permissions: form.permissions,
        });
        toast.success("Personel eklendi");
      }
      setDlgOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.deleteUser(delUser.id);
      toast.success("Personel silindi");
      setDelUser(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const doResetPw = async () => {
    try {
      await api.resetUserPassword(pwUser.id, newPw);
      toast.success("Şifre sıfırlandı");
      setPwUser(null);
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Şifre sıfırlanamadı");
    }
  };

  const permLabel = (key) => perms.find((p) => p.key === key)?.label || key;

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Personel Yönetimi"
        subtitle="Kullanıcı ekleyin, yetkilerini seçin ve şifrelerini yönetin"
        action={
          <Button data-testid="add-personnel-button" onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90">
            <UserPlus className="h-4 w-4" /> <span className="ml-1.5">Yeni Personel</span>
          </Button>
        }
      />

      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Yükleniyor...</div>
      ) : (
        <>
        <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/5 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400"><Wallet className="h-5 w-5" /></div>
              <div>
                <div className="font-medium">Bakiyeleri Yeniden Hesapla</div>
                <div className="text-xs text-muted-foreground">01.07.2026 öncesi tüm cari borç/alacakları sıfırlar; bu tarihten sonraki fatura ve eşleşen banka hareketlerinden yeniden hesaplar.</div>
                {rebuildResult && (
                  <div className="mt-2 text-xs text-emerald-400" data-testid="rebuild-result">
                    ✓ {rebuildResult.contacts_total} cari sıfırlandı · {rebuildResult.contacts_with_balance} carinin bakiyesi güncellendi · Alacak {money(rebuildResult.total_receivable)} · Borç {money(rebuildResult.total_payable)}{rebuildResult.statement_preserved ? ` · ${rebuildResult.statement_preserved} ekstre bakiyesi korundu` : ""}
                  </div>
                )}
              </div>
            </div>
            <Button data-testid="rebuild-balances-button" onClick={() => setRebuildOpen(true)} className="bg-amber-500 text-white hover:bg-amber-600">
              <RefreshCw className="h-4 w-4" /> <span className="ml-1.5">Yeniden Hesapla</span>
            </Button>
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><ShieldCheck className="h-5 w-5" /></div>
              <div>
                <div className="font-medium">Negatif Bakiye Koruması</div>
                <div className="text-xs text-muted-foreground">Açık olduğunda, bir müşteriden gelen banka tahsilatı borcundan fazlaysa cari bakiye sıfırın altına düşmez; fazlası müşteri kartına "avans" notu olarak eklenir.</div>
                <div className="mt-1 text-[11px] text-muted-foreground">Durum: <span className={preventNeg ? "text-emerald-400" : "text-muted-foreground"}>{preventNeg ? "Açık" : "Kapalı"}</span></div>
              </div>
            </div>
            <Button data-testid="toggle-prevent-negative-button" onClick={togglePreventNeg} disabled={prefSaving}
              className={preventNeg ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-secondary text-foreground hover:bg-secondary/80"}>
              <ShieldCheck className="h-4 w-4" /> <span className="ml-1.5">{preventNeg ? "Açık — Kapat" : "Kapalı — Aç"}</span>
            </Button>
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-sky-500/25 bg-sky-500/5 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 text-sky-400"><Database className="h-5 w-5" /></div>
              <div>
                <div className="font-medium">Veritabanı Yedekleme & Geri Yükleme</div>
                <div className="text-xs text-muted-foreground">Tüm veritabanını tek dosya olarak indirin veya daha önce aldığınız bir yedeği geri yükleyin.</div>
                <div className="mt-1 text-[11px] text-rose-400">Geri yükleme mevcut tüm verinin üzerine yazar.</div>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Button data-testid="backup-button" onClick={doBackup} disabled={backuping} variant="secondary" className="w-full sm:w-auto">
                <Download className="h-4 w-4" /> <span className="ml-1.5">{backuping ? "Hazırlanıyor..." : "Yedek İndir"}</span>
              </Button>
              <div className="flex items-center gap-2">
                <input data-testid="restore-file-input" type="file" accept="application/json,.json" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="max-w-[200px] text-xs text-muted-foreground file:mr-2 file:rounded-md file:border-0 file:bg-secondary file:px-2 file:py-1 file:text-xs file:text-foreground" />
                <Button data-testid="restore-button" onClick={doRestore} disabled={!restoreFile || restoring} className="bg-sky-500 text-white hover:bg-sky-600">
                  <Upload className="h-4 w-4" /> <span className="ml-1.5">{restoring ? "Yükleniyor..." : "Geri Yükle"}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {users.map((u) => {
            const isAdmin = u.role === "admin";
            return (
              <div key={u.id} data-testid="personnel-card" className="rounded-xl border border-border bg-card p-5">
                <div className="mb-3 flex items-start gap-3">
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isAdmin ? "bg-amber-500/15 text-amber-400" : "bg-primary/15 text-primary"}`}>
                    {isAdmin ? <ShieldCheck className="h-5 w-5" /> : <User className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{u.name || "Personel"}</div>
                    <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isAdmin ? "bg-amber-500/15 text-amber-300" : "bg-secondary text-muted-foreground"}`}>
                    {isAdmin ? "Yönetici" : "Personel"}
                  </span>
                </div>

                <div className="mb-4 min-h-[52px]">
                  {isAdmin ? (
                    <span className="text-xs text-muted-foreground">Tüm modüllere tam erişim</span>
                  ) : (u.permissions || []).length === 0 ? (
                    <span className="text-xs text-rose-400">Yetki atanmamış</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {u.permissions.map((k) => (
                        <span key={k} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-foreground">
                          {permLabel(k)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {!isAdmin && (
                  <div className="flex gap-2">
                    <Button data-testid="edit-personnel-button" variant="secondary" size="sm" onClick={() => openEdit(u)} className="flex-1">
                      <Pencil className="h-3.5 w-3.5" /> <span className="ml-1">Düzenle</span>
                    </Button>
                    <Button data-testid="reset-personnel-pw-button" variant="secondary" size="sm" onClick={() => setPwUser(u)}>
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    <Button data-testid="delete-personnel-button" variant="secondary" size="sm" onClick={() => setDelUser(u)} className="text-rose-400 hover:text-rose-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle>
            <DialogDescription>Personel bilgilerini girin ve erişebileceği menüleri seçin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>E-posta</Label>
              <Input
                data-testid="personnel-email-input"
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="ornek@arslan.ro"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>İsim</Label>
              <Input
                data-testid="personnel-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ad Soyad"
                className="mt-1.5"
              />
            </div>
            {!editing && (
              <div>
                <Label>Şifre</Label>
                <Input
                  data-testid="personnel-password-input"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="En az 6 karakter"
                  className="mt-1.5"
                />
              </div>
            )}
            <div>
              <Label className="mb-2 block">Yetkiler (erişebileceği menüler)</Label>
              <div className="grid grid-cols-2 gap-2">
                {perms.map((p) => (
                  <label
                    key={p.key}
                    data-testid={`perm-checkbox-${p.key}`}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm hover:bg-secondary/60"
                  >
                    <Checkbox
                      checked={form.permissions.includes(p.key)}
                      onCheckedChange={() => togglePerm(p.key)}
                    />
                    <span className="min-w-0 truncate">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDlgOpen(false)}>
              İptal
            </Button>
            <Button data-testid="save-personnel-button" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Şifre Sıfırla</DialogTitle>
            <DialogDescription>Personel için yeni bir şifre belirleyin.</DialogDescription>
          </DialogHeader>
          <div>
            <p className="mb-3 text-sm text-muted-foreground">{pwUser?.email} için yeni şifre belirleyin.</p>
            <Label>Yeni Şifre</Label>
            <Input
              data-testid="reset-pw-input"
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="En az 6 karakter"
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPwUser(null)}>
              İptal
            </Button>
            <Button data-testid="confirm-reset-pw-button" onClick={doResetPw} disabled={newPw.length < 6} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Sıfırla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rebuild balances confirm */}
      <AlertDialog open={rebuildOpen} onOpenChange={(o) => !o && setRebuildOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bakiyeler yeniden hesaplansın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              Tüm carilerin mevcut borç/alacak bakiyeleri <b>sıfırlanacak</b> ve yalnızca 01.07.2026'dan itibaren
              kesilen faturalar ile o tarihten sonraki eşleşen banka hareketlerinden yeniden hesaplanacak. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-rebuild-button" onClick={doRebuild} disabled={rebuilding} className="bg-amber-500 text-white hover:bg-amber-600">
              {rebuilding ? "Hesaplanıyor..." : "Evet, Yeniden Hesapla"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Personel silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {delUser?.name} ({delUser?.email}) hesabı kalıcı olarak silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-personnel-button" onClick={doDelete} className="bg-rose-500 text-white hover:bg-rose-600">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
